import React from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { sectionsLabel } from '../../utils/venueSections';
import { GroupedSlot, SLOT_TYPE_LABELS, slotStyle, timeOf } from './occupancy';

interface DayAgendaProps {
  day: Date;
  groups: GroupedSlot[];
  sectionCount: number;
  sectionNames?: string[];
  /** Foglalhat-e (stáb): ekkor a szabad sávokból indítható foglalás. */
  canBook: boolean;
  onBook: (start: Date) => void;
  onSelect: (group: GroupedSlot) => void;
  /** Heti listában: kisebb, és az üres napra csak egy sor jut. */
  compact?: boolean;
}

/** Ettől hosszabb szabad időt külön sorban mutatunk (percben). */
const MIN_GAP = 60;
const DAY_START = 8;
const DAY_END = 21;

/** Kis pályarajz: a foglalt részek kiemelve. */
export const MiniPitch: React.FC<{ count: number; occupied: number[] | null }> = ({ count, occupied }) => {
  if (count <= 1) return null;
  const cols = count === 3 ? 'grid-cols-3' : 'grid-cols-2';
  return (
    <span className={`grid ${cols} h-6 w-9 shrink-0 gap-px overflow-hidden rounded-[3px] bg-white ring-1 ring-gray-300`} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => i + 1).map((s) => (
        <span key={s} className={occupied === null || occupied.includes(s) ? 'bg-emerald-600' : 'bg-emerald-100'} />
      ))}
    </span>
  );
};

/**
 * Egy nap foglaltsága listában (mobilon és a heti listanézetben): időrendben a
 * foglalások, köztük a hosszabb szabad sávok - a stáb ezekből egy koppintással
 * foglalhat.
 */
const DayAgenda: React.FC<DayAgendaProps> = ({ day, groups, sectionCount, sectionNames, canBook, onBook, onSelect, compact = false }) => {
  // Szabad sávok a napon (ahol egyik rész sem foglalt), DAY_START és DAY_END között
  const gaps: { from: Date; to: Date }[] = [];
  const dayStart = new Date(day);
  dayStart.setHours(DAY_START, 0, 0, 0);
  const dayEnd = new Date(day);
  dayEnd.setHours(DAY_END, 0, 0, 0);
  let cursor = dayStart;
  for (const g of groups) {
    const s = new Date(g.slot.startTime);
    const e = new Date(g.slot.endTime);
    if (s > cursor && (s.getTime() - cursor.getTime()) / 60000 >= MIN_GAP && cursor < dayEnd) {
      gaps.push({ from: cursor, to: s < dayEnd ? s : dayEnd });
    }
    if (e > cursor) cursor = e;
  }
  if (cursor < dayEnd && (dayEnd.getTime() - cursor.getTime()) / 60000 >= MIN_GAP) gaps.push({ from: cursor, to: dayEnd });

  // A foglalások és a szabad sávok egy időrendi listában
  type Item = { kind: 'slot'; group: GroupedSlot; at: number } | { kind: 'gap'; from: Date; to: Date; at: number };
  const items: Item[] = [
    ...groups.map((group) => ({ kind: 'slot' as const, group, at: new Date(group.slot.startTime).getTime() })),
    ...(compact && groups.length === 0 ? [] : gaps.map((g) => ({ kind: 'gap' as const, ...g, at: g.from.getTime() }))),
  ].sort((a, b) => a.at - b.at);

  if (groups.length === 0) {
    return (
      <div className={`flex items-center justify-between gap-3 rounded-xl border border-dashed border-gray-300 bg-white px-4 ${compact ? 'py-2.5' : 'py-6'}`}>
        <span className="text-sm text-gray-500">Egész nap szabad</span>
        {canBook && (
          <button
            type="button"
            onClick={() => onBook(dayStart)}
            className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50"
          >
            <PlusIcon className="h-4 w-4" />
            Foglalás
          </button>
        )}
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {items.map((item) =>
        item.kind === 'slot' ? (
          <li key={`${item.group.slot.source}-${item.group.slot.id}`}>
            <button
              type="button"
              onClick={() => onSelect(item.group)}
              className={`flex w-full items-stretch gap-3 overflow-hidden rounded-xl bg-white text-left ring-1 ring-gray-200 transition hover:ring-primary-300 ${
                compact ? 'p-2.5' : 'p-3'
              }`}
            >
              <span className={`w-1 shrink-0 rounded-full ${slotStyle(item.group.slot.type).bar}`} aria-hidden="true" />
              <span className="w-12 shrink-0">
                <span className="block text-sm font-semibold tabular-nums text-gray-900">{timeOf(item.group.slot.startTime)}</span>
                <span className="block text-xs tabular-nums text-gray-400">{timeOf(item.group.slot.endTime)}</span>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-gray-900">{item.group.slot.title}</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
                  <span className={`rounded px-1.5 py-0.5 font-medium ${slotStyle(item.group.slot.type).pill}`}>
                    {SLOT_TYPE_LABELS[item.group.slot.type] ?? item.group.slot.type}
                  </span>
                  {item.group.slot.ageGroup && <span>{item.group.slot.ageGroup}</span>}
                  {sectionCount > 1 && (
                    <span className="truncate">
                      {item.group.sections === null ? 'Teljes pálya' : sectionsLabel(item.group.sections, sectionNames)}
                    </span>
                  )}
                </span>
              </span>
              <span className="flex items-center">
                <MiniPitch count={sectionCount} occupied={item.group.sections} />
              </span>
            </button>
          </li>
        ) : (
          <li key={`gap-${item.at}`}>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-gray-300 px-3 py-2">
              <span className="text-xs text-gray-500">
                Szabad · <span className="tabular-nums">{timeOf(item.from)}–{timeOf(item.to)}</span>
              </span>
              {canBook && (
                <button
                  type="button"
                  onClick={() => onBook(item.from)}
                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  Foglalás
                </button>
              )}
            </div>
          </li>
        )
      )}
    </ul>
  );
};

export default DayAgenda;
